import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

export const FeatureGate = ({ feature, children, showOverlay = true }) => {
  const { hasFeature, user } = useAuth();
  const navigate = useNavigate();

  if (hasFeature(feature)) {
    return children;
  }

  const featureNames = {
    calendar: 'Calendar',
    messaging: 'Messaging',
    collaboration: 'Collaboration',
    ai: 'AI Assistant',
    financials: 'Financial Reports',
    trust_accounting: 'Trust Accounting',
    reports: 'Advanced Reports',
    time_billing: 'Time & Billing',
  };

  const requiredTier = {
    calendar: 'Intermediate',
    messaging: 'Intermediate',
    collaboration: 'Intermediate',
    ai: 'Advanced',
    financials: 'Advanced',
    trust_accounting: 'Advanced',
    reports: 'Advanced',
    time_billing: 'Advanced',
  };

  if (showOverlay) {
    return (
      <div className="relative" data-testid={`feature-gate-${feature}`}>
        {/* Blurred background content */}
        <div className="filter blur-sm pointer-events-none opacity-50">
          {children}
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-lg">
          <Card className="max-w-md mx-4 shadow-lg">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-6 w-6 text-slate-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {featureNames[feature] || feature} Locked
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                This feature requires the <strong>{requiredTier[feature] || 'Advanced'}</strong> plan.
                Upgrade to unlock {featureNames[feature]?.toLowerCase() || 'this feature'} and more.
              </p>
              <Button 
                onClick={() => navigate('/settings', { state: { showUpgrade: true } })}
                className="gap-2"
                data-testid={`upgrade-btn-${feature}`}
              >
                Upgrade Plan
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-dashed" data-testid={`feature-gate-${feature}`}>
      <CardContent className="p-6 text-center">
        <Lock className="h-8 w-8 text-slate-400 mx-auto mb-3" />
        <p className="text-sm text-slate-600 mb-3">
          {featureNames[feature] || feature} requires {requiredTier[feature] || 'Advanced'} plan
        </p>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/settings', { state: { showUpgrade: true } })}
          data-testid={`upgrade-btn-${feature}`}
        >
          Upgrade
        </Button>
      </CardContent>
    </Card>
  );
};

export default FeatureGate;
